import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { BellOutlined } from "@ant-design/icons";

import BaseTrigger from "@/components_v2/Trigger/BaseTrigger";
import IndicatorBadge from "@/components_v2/Badge/IndicatorBadge";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { SIDEBAR_TRIGGER_VARIANT } from "../types";
import type { NotificationsSidebarTriggerProps } from "./types";

import "./styles.css";

const NotificationsSidebarTrigger = forwardRef<
  HTMLButtonElement,
  NotificationsSidebarTriggerProps
>(
  (
    {
      isCollapsed,
      variant = SIDEBAR_TRIGGER_VARIANT.SIDEBAR,
      isOpen,
      unreadCount,
      className,
      ...buttonProps
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isHeader = variant === SIDEBAR_TRIGGER_VARIANT.HEADER;
    const showLabel = !isHeader && !isCollapsed;

    const badgeBlock = (
      <IndicatorBadge
        className="notifications-bell-badge"
        color="var(--primary-500)"
        count={unreadCount}
      >
        <BellOutlined style={{ cursor: "pointer", fontSize: 20 }} />
      </IndicatorBadge>
    );

    return (
      <BaseTrigger
        ref={ref}
        aria-label={t("layouts.labels.notifications")}
        aria-expanded={isOpen}
        className={`notifications-trigger flex items-center gap-200 body-m-regular ${
          isCollapsed && !isHeader ? "notifications-trigger-collapsed" : ""
        } ${isHeader ? "notifications-trigger--header" : ""} ${className ?? ""}`}
        {...buttonProps}
      >
        <span className="notifications-trigger-icon-slot flex flex-shrink-0 justify-center items-center">
          {isHeader ? (
            <span className="notifications-trigger-icon">{badgeBlock}</span>
          ) : (
            <BaseTooltip
              title={t("layouts.labels.notifications")}
              placement="right"
              {...(isCollapsed ? {} : { open: false })}
            >
              <span className="notifications-trigger-icon">{badgeBlock}</span>
            </BaseTooltip>
          )}
        </span>

        {showLabel ? (
          <span className="notifications-trigger-label flex-1 one-line-ellipsis">
            {t("layouts.labels.notifications")}
          </span>
        ) : null}
      </BaseTrigger>
    );
  },
);

NotificationsSidebarTrigger.displayName = "NotificationsSidebarTrigger";

export default NotificationsSidebarTrigger;
